import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Button, Typography } from '@mui/material';
import { BiLinkExternal } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { Moves, Dealer_Viewable_Driver_Info } from '@gql/base-types';
import { useSubscribeToMoveDetailsSubscription } from '../../moves/queries/subscribeToMoveDetails.subscription.generated';
import { useGetMoveDetailsDriverLazyQuery } from '../../moves/queries/getMoveDetailsDriver.query.generated';
import MoveStatusTracker from '@components/MoveStatusTracker';
import MoveDetailsInfo from '@features/moves/components/MoveDetailsInfo';
import MoveDetailsLocation from '@features/moves/components/MoveDetailsLocation';
import MoveDetailsContacts from '@features/moves/components/MoveDetailsContacts';
import { Divider } from '@hopdrive/sdk-ui';
import { movePath } from '@features/products/productRoutes';

interface MoveDetailsPanelContentProps {
  move: Moves | null;
  onClose: () => void;
}

const MoveDetailsPanelContent = ({ move: initialMove, onClose }: MoveDetailsPanelContentProps) => {
  const navigate = useNavigate();
  const [driver, setDriver] = useState<Dealer_Viewable_Driver_Info>({});
  const [getDriver] = useGetMoveDetailsDriverLazyQuery();

  // Subscribe to move details for real-time updates
  const { data, loading } = useSubscribeToMoveDetailsSubscription({
    variables: { moveId: initialMove?.id || 0 },
    skip: !initialMove?.id,
  });

  // Use subscribed move data if available, otherwise use the initial move
  const move: Moves | undefined = data?.moves_by_pk || initialMove;

  // Fetch driver info when move changes
  useEffect(() => {
    const fetchDriver = async () => {
      if (!move?.id) {
        setDriver({});
        return;
      }

      try {
        const driverRes = await getDriver({ variables: { moveId: move.id } });
        if (driverRes?.data?.dealer_viewable_driver_info?.length) {
          setDriver(driverRes.data.dealer_viewable_driver_info[0]);
        } else {
          setDriver({});
        }
      } catch (err) {
        console.error('Failed to fetch driver info:', err);
        setDriver({});
      }
    };

    if (move?.id) {
      fetchDriver();
    }
  }, [move?.id, move?.driver_id, getDriver]);

  const handleViewFullDetails = () => {
    if (move?.id) {
      navigate(movePath(move.id));
      onClose();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!move) {
    return (
      <Typography variant="body2" color="text.secondary">
        No move selected
      </Typography>
    );
  }

  return (
    <>
      {/* Status Tracker */}
      <Box sx={{ mb: 3 }}>
        <MoveStatusTracker move={move} size="lg" />
      </Box>

      {/* Basic Info */}
      <MoveDetailsInfo
        move={move}
        driver={driver}
        isEditing={false}
        compact={true}
        editContact=""
        editNotes=""
        editVin=""
        setEditContact={() => {}}
        setEditNotes={() => {}}
        setEditVin={() => {}}
      />

      {/* Pickup & Delivery */}
      <Divider spacer mb={3} mt={3}>
        Pickup & Delivery
      </Divider>
      <Box sx={{ mb: 2 }}>
        <MoveDetailsLocation move={move} type="pickup" />
      </Box>
      <Box sx={{ mb: 2 }}>
        <MoveDetailsLocation move={move} type="delivery" />
      </Box>

      {/* Contact Information */}
      {move?.contactstomoves?.length && !move?.consumer_pickup ? (
        <>
          <Divider spacer mb={3} mt={3}>
            Contact Information
          </Divider>
          <MoveDetailsContacts move={move} />
        </>
      ) : null}

      {/* View Full Details Button */}
      <Box sx={{ mt: 4 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<BiLinkExternal />}
          onClick={handleViewFullDetails}
        >
          View Full Details
        </Button>
      </Box>
    </>
  );
};

export default MoveDetailsPanelContent;
